import Backbone from 'backbone'
import template from 'scoreboard/datasets/edit/fields/categories-and-ranges-form.hbs'
import _ from 'lodash'
import fieldRange from './field-range.hbs'
import formatValue from 'scoreboard/components/format-value'
import RangeForm from './range-form'
import appDisplay from 'scoreboard/components/app-display'
import i18n from 'scoreboard/components/i18n'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import rangeKeySuffix from '../range-key-suffix'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import FormView from 'scoreboard/components/form-view'
import formHelper from 'scoreboard/components/form-helper'
import Dataset from 'scoreboard/datasets/model/dataset'
const { BLANK } = Dataset
import DataType from 'scoreboard/datasets/model/data-type'
const { TEXT } = DataType

/**
 * Form that is shown when clicking on the link in the Categories & Ranges column
 */
var CategoriesAndRangesForm = Backbone.View.extend({

	tagName: 'form',

	className: 'categories-and-ranges-form',

	events: {
		'submit': 'submit',
		'click .add-range': 'addRange',
		'click .field-range .edit': 'editRange',
		'click .field-range .delete': 'deleteRange'
	},

	/** @type {Dataset} */
	model: null,

	// this.options.field is a plain object containing the field info
	initialize: function(options) {
		this.options = options || {}
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })
		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			allowCleanSubmission: true
		})
	},

	render: function() {
		this.$el.empty().append(template(this.context()))

		this.renderRanges()
		this.formView.start()

		return this
	},

	renderRanges: function() {
		this.$('.no-ranges').toggleClass('is-not-displayed',
			!_.isEmpty(this.options.field.ranges))

		var html = ''
		// make sure our list of ranges is sorted alphabetically.  this is
		// important for the display, and also when we edit and delete ranges
		// we look for things by index, so we want this.options.field.ranges to
		// be sorted
		this.options.field.ranges = _.sortBy(this.options.field.ranges, function(range) {
			if (this.options.field.dataType === TEXT) {
				return range.label.toLowerCase()
			}
			if (isNumericDataType(this.options.field.dataType)) {
				return Number(_.isNil(range.high) ? Number.MAX_VALUE : range.high)
			}
		}.bind(this))
		_.each(this.options.field.ranges, function(range) {
			var description
			if (isNumericDataType(this.options.field.dataType)) {
				var format = function(value) {
					return formatValue(_.extend({ value: value }, {
						dataType: this.options.field.dataType,
						currencyCode: this.options.field.currencyCode
					}))
				}.bind(this)
				if (range.low == null) {
					description = i18n.get('datasets.filters.pretty.numeric.lessThan',
						format(range.high))
				} else if (range.high == null) {
					description = i18n.get('datasets.filters.pretty.numeric.greaterThanOrEquals',
						format(range.low))
				} else {
					description = i18n.get('datasets.fields.ranges.numeric.description',
						format(range.low), format(range.high))
				}

				if (range.includeBlank) {
					description += `, ${i18n.get('common.blank').toUpperCase()}`
				}
			}
			if (this.options.field.dataType === TEXT) {
				var sorted = _.sortBy(range.content, function(value) {
					// #29612 returning an empty string causes this element to
					// be first in the sort
					return value === BLANK ? '' : value.toLowerCase()
				})
				sorted = _.map(sorted, _.escape)
				description = _.join(sorted, ', ')
					// #29612 we need to replace the blank key value with the normal
					// blank text
					.replace(BLANK,
						`<span class="blank-value">
							${i18n.get('common.blank')}
						</span>`)
			}
			html += fieldRange({
				label: range.label,
				description: description
			})
		}.bind(this))
		this.$('.list').html(html)
	},

	context: function() {
		// resolves to "Breakdown Sets" or "Breakdown Ranges"
		let suffix = rangeKeySuffix(this.options.field.dataType)
		let rangeKey = 'datasets.fields.ranges' + suffix
		let addOrEdit = this.options.field.ranges && this.options.field.ranges.length ? 'edit' : 'add'
		return {
			title: i18n.get(`datasets.fields.ranges.${addOrEdit}Ranges${suffix}`, this.options.field.label),
			panelTitle: i18n.get(rangeKey),
			thereAreNoMsg: i18n.get('common.thereAreNo', rangeKey)
		}
	},

	addRange: function() {
		var range = {}
		if (!_.isEmpty(this.options.field.ranges)) {
			// set the default low of the next range to be equal to the highest
			// high value in the ranges
			range.low = _.max(_.map(this.options.field.ranges, 'high'))
		}
		var form = new RangeForm({
			model: this.model,
			field: this.options.field,
			range: range
		})
		appDisplay.dialog(form)
		form.on('done', function(range) {
			if (_.isNil(this.options.field.ranges)) {
				this.options.field.ranges = [range]
			} else {
				this.options.field.ranges.push(range)
			}
			this.renderRanges()
		}.bind(this))
	},

	editRange: function(e) {
		var index = this._fieldRangeIndex(e)
		var form = new RangeForm({
			model: this.model,
			field: this.options.field,
			range: this.options.field.ranges[index]
		})
		appDisplay.dialog(form)
		form.on('done', function(range) {
			this.options.field.ranges[index] = range
			this.renderRanges()
		}.bind(this))
	},

	deleteRange: function(e) {
		var index = this._fieldRangeIndex(e)
		this.options.field.ranges.splice(index, 1)
		this.renderRanges()
	},

	// based on the given event on a field-range row, returns the index of the
	// corresponding range within this.options.field
	_fieldRangeIndex: function(e) {
		return this.$(e.target).closest('.field-range').index()
	},

	saveState: function() {
		_.extend(this.options.field, formHelper.readFromDom(this.el))
	},

	submit: function(e) {
		e.preventDefault()
		this.saveState()
		this.trigger('done', this.options.field)
		this.formView.submit(e)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.wizardProgress, this.covertButtonTooltips], 'remove')
	}

})

export default CategoriesAndRangesForm

CategoriesAndRangesForm.__filename__ = 'datasets/edit/fields/categories-and-ranges-form.js'
